import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { entityClient } from '../services/entityClient';
import { Plus, Edit2, Trash2, X, UserPlus, User, Shield } from 'lucide-react';

const Employees = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [creatingUserAccount, setCreatingUserAccount] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    position: '',
    department: '',
    salary: '',
    hire_date: '',
    create_user_account: false,
    user_role: 'assistant'
  });
  const [userRoleForExisting, setUserRoleForExisting] = useState('assistant');

  const userRoles = [
    { value: 'home', label: 'Home' },
    { value: 'manager', label: 'Manager' },
    { value: 'accountant', label: 'Accountant' },
    { value: 'project_manager', label: 'Project Manager' },
    { value: 'sales_rep', label: 'Sales Rep' },
    { value: 'technical', label: 'Technical' },
    { value: 'hr', label: 'HR' },
    { value: 'engineer', label: 'Engineer' },
    { value: 'assistant', label: 'Assistant' }
  ];

  useEffect(() => {
    fetchEmployees();
    
    // Auto-refresh every 60 seconds
    const interval = setInterval(() => {
      fetchEmployees();
    }, 60000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await axiosInstance.get('/employees');
      setEmployees(response.data);
    } catch (error) {
      if (loading) {
        toast.error('Failed to fetch employees');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEmployee) {
        await axiosInstance.put(`/employees/${editingEmployee.id}`, formData);
        toast.success('Employee updated successfully');
      } else {
        const response = await axiosInstance.post('/employees', formData);
        if (response.data.approval_status === 'pending') {
          toast.success('Employee submitted for approval', {
            description: 'Waiting for admin approval before it appears in the list'
          });
        } else {
          if (formData.create_user_account) {
            toast.success('Employee added with user account!', {
              description: 'Default password: password123'
            });
          } else {
            toast.success('Employee added successfully');
          }
        }
      }
      setShowModal(false);
      resetForm();
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (employee) => {
    const id = typeof employee === 'string' ? employee : employee.id;
    const displayName = typeof employee === 'object'
      ? (employee.full_name || employee.name || employee.id)
      : undefined;
    const res = await entityClient.delete('employee', id, { displayName });
    if (res.mode === 'deleted') { toast.success('تم حذف الموظف'); fetchEmployees(); }
    else if (res.mode === 'archived') { fetchEmployees(); }
    else if (res.mode === 'error') { toast.error('فشل الحذف: ' + (res.error?.response?.data?.detail || res.error?.message || '')); }
  };

  const handleEdit = (employee) => {
    setEditingEmployee(employee);
    setFormData({
      full_name: employee.full_name,
      email: employee.email,
      phone: employee.phone || '',
      position: employee.position,
      department: employee.department,
      salary: employee.salary,
      hire_date: employee.hire_date,
      create_user_account: false,
      user_role: 'assistant'
    });
    setShowModal(true);
  };

  const handleCreateUserAccount = (employee) => {
    setSelectedEmployee(employee);
    setUserRoleForExisting('assistant');
    setShowUserModal(true);
  };

  const submitCreateUserAccount = async () => {
    if (!selectedEmployee) return;
    
    setCreatingUserAccount(true);
    try {
      const response = await axiosInstance.post(
        `/employees/${selectedEmployee.id}/create-user-account`,
        { role: userRoleForExisting }
      );
      toast.success('User account created!', {
        description: `Username: ${response.data.username}, Password: ${response.data.default_password}`
      });
      setShowUserModal(false);
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user account');
    } finally {
      setCreatingUserAccount(false);
    }
  };

  const resetForm = () => {
    setFormData({
      full_name: '',
      email: '',
      phone: '',
      position: '',
      department: '',
      salary: '',
      hire_date: '',
      create_user_account: false,
      user_role: 'assistant'
    });
    setEditingEmployee(null);
  };

  if (loading) {
    return <div className="text-center py-12">Loading employees...</div>;
  }

  return (
    <div data-testid="employees-page">
      <div className="page-header">
        <h2 className="page-title">Employees</h2>
        <button
          data-testid="add-employee-button"
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="btn-primary"
        >
          <Plus className="w-5 h-5 inline mr-2" />
          New Employee
        </button>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Position</th>
              <th>Department</th>
              <th>Salary</th>
              <th>Hire Date</th>
              <th>User Account</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {employees.length > 0 ? (
              employees.map((employee) => (
                <tr key={employee.id} data-testid={`employee-row-${employee.id}`}>
                  <td className="font-semibold">{employee.full_name}</td>
                  <td>{employee.email}</td>
                  <td>{employee.position}</td>
                  <td>{employee.department}</td>
                  <td>{employee.salary.toLocaleString()} SAR</td>
                  <td>{new Date(employee.hire_date).toLocaleDateString()}</td>
                  <td>
                    {employee.user_id ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                        <User className="w-3 h-3" />
                        Has Account
                      </span>
                    ) : (
                      <button
                        onClick={() => handleCreateUserAccount(employee)}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors"
                        data-testid={`create-user-account-${employee.id}`}
                      >
                        <UserPlus className="w-3 h-3" />
                        Create Account
                      </button>
                    )}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        data-testid={`edit-employee-${employee.id}`}
                        onClick={() => handleEdit(employee)}
                        className="btn-icon edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`delete-employee-${employee.id}`}
                        onClick={() => handleDelete(employee)}
                        className="btn-icon delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="text-center py-12 text-gray-500">
                  No employees found. Add your first employee!
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Employee Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                {editingEmployee ? 'Edit Employee' : 'New Employee'}
              </h3>
              <button
                data-testid="close-modal-button"
                onClick={() => setShowModal(false)}
                className="btn-icon"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Full Name</label>
                <input
                  data-testid="employee-name-input"
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  data-testid="employee-email-input"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Phone</label>
                <input
                  data-testid="employee-phone-input"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="form-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="form-group">
                  <label className="form-label">Position</label>
                  <input
                    data-testid="employee-position-input"
                    type="text"
                    value={formData.position}
                    onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                    className="form-input"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Department</label>
                  <input
                    data-testid="employee-department-input"
                    type="text"
                    value={formData.department}
                    onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                    className="form-input"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="form-group">
                  <label className="form-label">Salary (SAR)</label>
                  <input
                    data-testid="employee-salary-input"
                    type="number"
                    value={formData.salary}
                    onChange={(e) => setFormData({ ...formData, salary: e.target.value })}
                    className="form-input"
                    required
                    min="0"
                    step="0.01"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Hire Date</label>
                  <input
                    data-testid="employee-hire-date-input"
                    type="date"
                    value={formData.hire_date}
                    onChange={(e) => setFormData({ ...formData, hire_date: e.target.value })}
                    className="form-input"
                    required
                  />
                </div>
              </div>

              {/* Create User Account Section - Only for new employees */}
              {!editingEmployee && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                  <div className="flex items-center gap-3 mb-3">
                    <input
                      type="checkbox"
                      id="create_user_account"
                      checked={formData.create_user_account}
                      onChange={(e) => setFormData({ ...formData, create_user_account: e.target.checked })}
                      className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      data-testid="create-user-account-checkbox"
                    />
                    <label htmlFor="create_user_account" className="font-medium text-blue-800 flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      Create System User Account
                    </label>
                  </div>
                  
                  {formData.create_user_account && (
                    <div className="ml-8">
                      <label className="form-label text-blue-700">User Role</label>
                      <select
                        value={formData.user_role}
                        onChange={(e) => setFormData({ ...formData, user_role: e.target.value })}
                        className="form-select"
                        data-testid="user-role-select"
                      >
                        {userRoles.map(role => (
                          <option key={role.value} value={role.value}>{role.label}</option>
                        ))}
                      </select>
                      <p className="text-xs text-blue-600 mt-2">
                        * Default password will be: <strong>password123</strong>
                      </p>
                      <p className="text-xs text-blue-600">
                        * Employee will appear in Settings → Employee Permissions
                      </p>
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-3 mt-6">
                <button
                  data-testid="submit-employee-button"
                  type="submit"
                  className="btn-primary flex-1"
                >
                  {editingEmployee ? 'Update Employee' : 'Add Employee'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create User Account Modal for Existing Employee */}
      {showUserModal && selectedEmployee && (
        <div className="modal-overlay" onClick={() => setShowUserModal(false)}>
          <div className="modal-content max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-blue-600" />
                Create User Account
              </h3>
              <button onClick={() => setShowUserModal(false)} className="btn-icon">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="py-4">
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="text-sm text-gray-600">Creating user account for:</p>
                <p className="font-semibold text-lg">{selectedEmployee.full_name}</p>
                <p className="text-sm text-gray-500">{selectedEmployee.email}</p>
              </div>

              <div className="form-group">
                <label className="form-label">Select User Role</label>
                <select
                  value={userRoleForExisting}
                  onChange={(e) => setUserRoleForExisting(e.target.value)}
                  className="form-select"
                  data-testid="existing-user-role-select"
                >
                  {userRoles.map(role => (
                    <option key={role.value} value={role.value}>{role.label}</option>
                  ))}
                </select>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-4">
                <p className="text-sm text-yellow-800">
                  <strong>Note:</strong> Default password will be <strong>password123</strong>
                </p>
                <p className="text-xs text-yellow-700 mt-1">
                  Please ask the employee to change it after first login.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={submitCreateUserAccount}
                disabled={creatingUserAccount}
                className="btn-primary flex-1"
                data-testid="confirm-create-user-account"
              >
                {creatingUserAccount ? 'Creating...' : 'Create Account'}
              </button>
              <button
                onClick={() => setShowUserModal(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Employees;
